/**
 * Tableau de bord — VUE D'ENSEMBLE de l'activité de l'électricien.
 *
 * Un vrai tableau de bord : des icônes pour repérer chaque chiffre d'un coup
 * d'œil, et des graphiques pour lire les tendances — courbe des commissions,
 * anneaux devis/commandes par statut et rythme hebdomadaire.
 *
 * Règle de style : AUCUNE bordure, aucun cadre. Les surfaces sont des
 * `.pro-card` (blanc + rayon, sans trait) posées sur le crème du fond ; la
 * séparation se fait par le contraste et le vide, pas par des lignes.
 *
 * ⚠ Le portefeuille est CUMULÉ depuis l'origine, jamais filtré par la période :
 * un « solde disponible ce mois-ci » n'existe pas. Tout le reste suit le filtre.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  Banknote,
  CalendarDays,
  ClipboardList,
  Clock,
  FileText,
  Hourglass,
  Receipt,
  ShoppingBag,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import {
  COMMANDE_STATUSES,
  DEVIS_STATUSES,
  computeMonthlySeries,
  computeStatusBreakdown,
  computeWeekdayDistribution,
  filterByRange,
  percentChange,
  resolveStatsRange,
  type ProStatsRange,
} from '@erp/domain/pro';
import { PeriodPicker } from '@/components/common/PeriodPicker';
import { CommandeStatusBadge } from '@/components/common/StatusBadge';
import { useDataService } from '@/lib/service-context';
import { useProAuth } from '@/lib/auth-context';
import { formatDate, formatTND } from '@/lib/format';
import { useProDashboard } from '@/hooks/use-pro-dashboard';
import {
  ChartSkeleton,
  DEVIS_STATUS_COLORS,
  DonutCard,
  LegendDot,
  MonthTooltip,
  ORDER_STATUS_COLORS,
  clientLabel,
  fmtInt,
  fmtPct,
  makeMonthLabel,
  makeWeekdayLabels,
} from './stats/stats-ui';

/** Sépare les millimes pour les composer plus petit : « 4 300,834 DT » tout en
 *  gros noie le chiffre utile sous ses décimales. */
function splitAmount(value: number): { main: string; cents: string } {
  const [main = '0', rest = '000 DT'] = formatTND(value).split(',');
  return { main, cents: rest };
}

export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const ds = useDataService();
  const { proCustomer } = useProAuth();

  const [range, setRange] = useState<ProStatsRange>('this_month');
  const dashboard = useProDashboard(range);
  const { activity, wallet: walletSummary, clients, previousActivity } = dashboard;

  const clientsQuery = useQuery({
    queryKey: ['pro-clients'],
    queryFn: () => ds.proClients.list(),
    staleTime: 60_000,
  });

  const clientNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of clientsQuery.data ?? []) {
      map.set(c.id, [c.prenom, c.nom].filter(Boolean).join(' ').trim() || c.nom);
    }
    return map;
  }, [clientsQuery.data]);

  const rangeOptions = useMemo(
    () => [
      { key: 'this_week' as const, label: t('pro.statistique.range.this_week', 'Cette semaine') },
      { key: 'this_month' as const, label: t('pro.statistique.range.this_month', 'Ce mois') },
      { key: 'last_month' as const, label: t('pro.statistique.range.last_month', 'Mois dernier') },
      { key: 'this_year' as const, label: t('pro.statistique.range.this_year', 'Cette année') },
      { key: 'all' as const, label: t('pro.statistique.range.all', 'Depuis toujours') },
    ],
    [t],
  );

  const monthly = useMemo(
    () =>
      computeMonthlySeries(dashboard.commandes, dashboard.walletMovements, {
        months: 12,
        devis: dashboard.devis,
      }),
    [dashboard.commandes, dashboard.walletMovements, dashboard.devis],
  );

  // Les répartitions suivent la période choisie, comme les compteurs.
  const bounds = useMemo(() => resolveStatsRange(range, new Date()), [range]);
  const periodCommandes = useMemo(() => filterByRange(dashboard.commandes, bounds), [dashboard.commandes, bounds]);
  const periodDevis = useMemo(() => filterByRange(dashboard.devis, bounds), [dashboard.devis, bounds]);

  const devisSlices = useMemo(() => computeStatusBreakdown(periodDevis, DEVIS_STATUSES), [periodDevis]);
  const orderSlices = useMemo(() => computeStatusBreakdown(periodCommandes, COMMANDE_STATUSES), [periodCommandes]);
  const weekdays = useMemo(() => computeWeekdayDistribution(periodCommandes), [periodCommandes]);
  const maxWeekday = Math.max(...weekdays.map((p) => p.orders));

  const topClients = useMemo(() => clients.filter((row) => row.revenueTtc > 0).slice(0, 5), [clients]);
  const recentOrders = useMemo(() => dashboard.commandes.slice(0, 5), [dashboard.commandes]);
  const monthLabel = useMemo(() => makeMonthLabel(i18n.language), [i18n.language]);
  const weekdayLabels = useMemo(() => makeWeekdayLabels(i18n.language), [i18n.language]);

  const delta = (current: number, key: keyof typeof activity) =>
    previousActivity ? percentChange(current, Number(previousActivity[key])) : null;

  const balance = splitAmount(walletSummary.available);
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return t('pro.dashboard.greeting_morning', 'Bonjour');
    if (hour < 18) return t('pro.dashboard.greeting_afternoon', 'Bon après-midi');
    return t('pro.dashboard.greeting_evening', 'Bonsoir');
  }, [t]);

  if (dashboard.isError) {
    return (
      <p className="py-16 text-center text-[14px]" style={{ color: 'var(--pro-danger)' }}>
        {t('pro.dashboard.load_error', 'Impossible de charger votre tableau de bord.')}
      </p>
    );
  }

  return (
    <div className="space-y-4 pb-4">
      {/* ---------- Portefeuille : le solde en grand + sa décomposition ---------- */}
      <section className="grid lg:grid-cols-[1.15fr_2fr] gap-4 pro-rise-in" style={{ ['--pro-stagger-i' as string]: 0 }}>
        <div className="pro-card p-6 flex flex-col justify-between gap-5">
          <div>
            <div className="flex items-center gap-2.5">
              <IconBubble icon={Wallet} accent />
              <span className="text-[13px]" style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}>
                {t('pro.statistique.wallet_available', 'Solde disponible')}
              </span>
            </div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="pro-numeral pro-display" style={{ color: 'var(--pro-text-dark)' }}>
                {balance.main}
              </span>
              <span className="pro-numeral text-[19px] font-medium" style={{ color: 'var(--pro-text-light)' }}>
                ,{balance.cents}
              </span>
            </div>
            <p className="text-[12.5px] mt-2" style={{ color: 'var(--pro-text-light)', fontFamily: 'Figtree' }}>
              {greeting}
              {proCustomer?.firstName ? `, ${proCustomer.firstName}` : ''} — {t('pro.statistique.wallet_available_hint', 'Utilisable sur vos commandes')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/portefeuille')}
            className="inline-flex items-center justify-center gap-1.5 h-11 rounded-xl text-[13.5px] font-semibold text-white"
            style={{ background: 'var(--pro-primary)', fontFamily: 'Figtree' }}
          >
            {t('pro.dashboard.open_wallet', 'Portefeuille')}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <WalletTile
            icon={Receipt}
            label={t('pro.statistique.wallet_earned', 'Total gagné')}
            value={formatTND(walletSummary.earned)}
            accent
          />
          <WalletTile
            icon={BadgeCheck}
            label={t('pro.statistique.wallet_validated', 'Commission validée')}
            value={formatTND(walletSummary.validated)}
          />
          <WalletTile
            icon={Hourglass}
            label={t('pro.statistique.wallet_pending', 'Commission en attente')}
            value={formatTND(walletSummary.pending)}
          />
          <div className="sm:col-span-3 pro-card p-5 flex flex-wrap items-center gap-x-8 gap-y-3">
            <InlineFact
              icon={Banknote}
              label={t('pro.statistique.wallet_used', 'Commission utilisée')}
              value={formatTND(walletSummary.used + walletSummary.withdrawn)}
            />
            <InlineFact
              icon={ClipboardList}
              label={t('pro.statistique.wallet_movements', 'Mouvements')}
              value={fmtInt(walletSummary.movementsCount)}
            />
            {walletSummary.rejected > 0 && (
              <InlineFact
                icon={Clock}
                label={t('pro.statistique.wallet_rejected', 'Commission refusée')}
                value={formatTND(walletSummary.rejected)}
                danger
              />
            )}
          </div>
        </div>
      </section>

      {/* ---------- Activité ---------- */}
      <section className="space-y-4 pro-rise-in" style={{ ['--pro-stagger-i' as string]: 1 }}>
        <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
          <h2
            className="text-[17px] font-bold"
            style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree', letterSpacing: '-0.01em' }}
          >
            {t('pro.dashboard.activity_title', 'Mon activité')}
          </h2>
          <PeriodPicker value={range} options={rangeOptions} onChange={setRange} />
        </div>

        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <Metric
            icon={ShoppingBag}
            label={t('pro.statistique.total_commandes', 'Total des commandes')}
            value={fmtInt(activity.ordersCount)}
            trend={delta(activity.ordersCount, 'ordersCount')}
            hint={t('pro.statistique.orders_pending_hint', '{{value}} en attente', { value: fmtInt(activity.ordersPending) })}
            loading={dashboard.isLoading}
          />
          <Metric
            icon={FileText}
            label={t('pro.statistique.total_devis', 'Total des devis')}
            value={fmtInt(activity.quotesCount)}
            trend={delta(activity.quotesCount, 'quotesCount')}
            hint={t('pro.statistique.quotes_open_hint', '{{value}} en attente', { value: fmtInt(activity.quotesPending) })}
            loading={dashboard.isLoading}
          />
          <Metric
            icon={TrendingUp}
            label={t('pro.statistique.total_revenue', "Chiffre d'affaires TTC")}
            value={formatTND(activity.revenueTtc)}
            trend={delta(activity.revenueTtc, 'revenueTtc')}
            hint={t('pro.statistique.aov_hint', 'Panier moyen {{value}}', { value: formatTND(activity.averageOrderValue) })}
            loading={dashboard.isLoading}
          />
          <Metric
            icon={Receipt}
            accent
            label={t('pro.statistique.total_commission', 'Commission générée')}
            value={formatTND(activity.commissionOnOrders)}
            trend={delta(activity.commissionOnOrders, 'commissionOnOrders')}
            hint={t('pro.statistique.commission_rate_hint', '{{value}} du CA', { value: fmtPct(activity.commissionRateOnRevenue) })}
            loading={dashboard.isLoading}
          />
        </div>

        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <Metric
            small
            icon={Banknote}
            label={t('pro.statistique.collected', 'Encaissé auprès des clients')}
            value={formatTND(activity.collected)}
            hint={t('pro.statistique.collected_hint', 'dont {{value}} réglés au portefeuille', {
              value: formatTND(activity.paidWithWallet),
            })}
          />
          <Metric
            small
            icon={Users}
            label={t('pro.statistique.active_clients', 'Clients actifs')}
            value={fmtInt(activity.activeClientsCount)}
            hint={t('pro.statistique.clients_total_hint', '{{value}} au total', { value: fmtInt(activity.clientsCount) })}
          />
          <Metric
            small
            icon={ClipboardList}
            label={t('pro.statistique.conversion', 'Devis convertis')}
            value={fmtPct(activity.quoteConversionRate)}
            hint={t('pro.statistique.conversion_hint', '{{value}} devis émis', { value: fmtInt(activity.quotesCount) })}
          />
          <Metric
            small
            icon={Clock}
            label={t('pro.statistique.pipeline', 'Commission à venir')}
            value={formatTND(activity.commissionOnPendingQuotes)}
            hint={t('pro.statistique.pipeline_hint', 'Devis non encore transformés')}
          />
        </div>
      </section>

      {/* ---------- Courbe des commissions ---------- */}
      <section className="pro-card p-6 pro-rise-in" style={{ ['--pro-stagger-i' as string]: 2 }}>
        <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
          <div className="flex items-center gap-3">
            <IconBubble icon={TrendingUp} accent />
            <div>
              <span className="text-[13px]" style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}>
                {t('pro.dashboard.commissions_title', 'Commissions des 12 derniers mois')}
              </span>
              <div className="pro-numeral text-[26px] font-bold leading-tight" style={{ color: 'var(--pro-text-dark)' }}>
                {formatTND(walletSummary.earned)}
              </div>
            </div>
          </div>
          <Quiet label={t('pro.dashboard.see_details', 'Voir le détail')} onClick={() => navigate('/statistique')} />
        </div>
        <div className="h-[240px]">
          {dashboard.isLoading ? (
            <ChartSkeleton />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthly} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="dashCommissionValidated" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF6B1E" stopOpacity={0.28} />
                    <stop offset="100%" stopColor="#FF6B1E" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="dashCommissionPending" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F6A723" stopOpacity={0.2} />
                    <stop offset="100%" stopColor="#F6A723" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 6" stroke="#EFEBE6" vertical={false} />
                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#A9A49E', fontSize: 11 }}
                  tickFormatter={monthLabel}
                  minTickGap={16}
                  dy={8}
                />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#A9A49E', fontSize: 11 }} width={44} />
                <Tooltip
                  cursor={{ stroke: '#E4DFD9', strokeWidth: 1 }}
                  content={
                    <MonthTooltip
                      monthLabel={monthLabel}
                      validatedLabel={t('pro.statistique.wallet_validated', 'Commission validée')}
                      pendingLabel={t('pro.statistique.wallet_pending', 'Commission en attente')}
                      ordersLabel={t('pro.statistique.orders_label', 'Commandes')}
                    />
                  }
                />
                <Area
                  isAnimationActive={false}
                  type="monotone"
                  dataKey="commissionValidated"
                  stackId="commission"
                  stroke="#FF6B1E"
                  strokeWidth={2.5}
                  fill="url(#dashCommissionValidated)"
                  dot={false}
                  activeDot={{ r: 5, fill: '#FF6B1E', stroke: '#fff', strokeWidth: 2 }}
                />
                <Area
                  isAnimationActive={false}
                  type="monotone"
                  dataKey="commissionPending"
                  stackId="commission"
                  stroke="#F6A723"
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  fill="url(#dashCommissionPending)"
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-5 mt-4">
          <LegendDot color="#FF6B1E" label={t('pro.statistique.wallet_validated', 'Commission validée')} />
          <LegendDot color="#F6A723" label={t('pro.statistique.wallet_pending', 'Commission en attente')} />
        </div>
      </section>

      {/* ---------- Répartitions par statut ---------- */}
      <div className="grid lg:grid-cols-2 gap-4 pro-rise-in" style={{ ['--pro-stagger-i' as string]: 3 }}>
        <DonutCard
          title={t('pro.statistique.devis_breakdown', 'Devis par statut')}
          slices={devisSlices}
          colors={DEVIS_STATUS_COLORS}
          labelFor={(status) => t(`pro.devis.status.${status}`, status)}
          emptyLabel={t('pro.statistique.empty', 'Pas encore de données')}
        />
        <DonutCard
          title={t('pro.statistique.orders_breakdown', 'Commandes par statut')}
          slices={orderSlices}
          colors={ORDER_STATUS_COLORS}
          labelFor={(status) => t(`pro.commande.status.${status}`, status)}
          emptyLabel={t('pro.statistique.empty', 'Pas encore de données')}
        />
      </div>

      {/* ---------- Rythme hebdomadaire + meilleurs clients ---------- */}
      <div className="grid lg:grid-cols-2 gap-4 items-start pro-rise-in" style={{ ['--pro-stagger-i' as string]: 4 }}>
        <section className="pro-card p-6">
          <div className="flex items-center gap-3 mb-5">
            <IconBubble icon={CalendarDays} />
            <h2
              className="text-[15px] font-semibold"
              style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree', letterSpacing: '-0.01em' }}
            >
              {t('pro.statistique.orders_overview', 'Commandes par jour de la semaine')}
            </h2>
          </div>
          <div className="h-[190px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weekdays.map((p) => ({ ...p, label: weekdayLabels[p.weekday] ?? '' }))}>
                <CartesianGrid strokeDasharray="2 6" stroke="#EFEBE6" vertical={false} />
                <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#A9A49E', fontSize: 11 }} dy={6} />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#A9A49E', fontSize: 11 }}
                  allowDecimals={false}
                  width={28}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(255,107,30,0.05)' }}
                  contentStyle={{ background: '#fff', border: 'none', borderRadius: 12, fontSize: 12, boxShadow: '0 6px 20px rgba(42,29,20,0.12)' }}
                  formatter={(value) => [fmtInt(Number(value)), t('pro.statistique.orders_label', 'Commandes')]}
                />
                {/* Sans animation : recharts ne peint la géométrie qu'au fil des
                    frames, un onglet au rAF bridé afficherait des barres vides. */}
                <Bar dataKey="orders" radius={[6, 6, 0, 0]} isAnimationActive={false}>
                  {weekdays.map((point) => (
                    <Cell
                      key={point.weekday}
                      fill={point.orders > 0 && point.orders === maxWeekday ? '#FF6B1E' : '#FFDCC4'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="pro-card p-6">
          <div className="flex items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-3">
              <IconBubble icon={Users} />
              <h2
                className="text-[15px] font-semibold"
                style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree', letterSpacing: '-0.01em' }}
              >
                {t('pro.dashboard.top_clients', 'Meilleurs clients')}
              </h2>
            </div>
            <Quiet label={t('pro.dashboard.see_details', 'Voir le détail')} onClick={() => navigate('/statistique')} />
          </div>
          {dashboard.isLoading ? (
            <Empty label={t('pro.common.loading', 'Chargement…')} />
          ) : topClients.length === 0 ? (
            <Empty label={t('pro.statistique.clients_empty', 'Aucun client sur cette période')} />
          ) : (
            <ul className="space-y-4">
              {topClients.map((row, index) => (
                <li key={row.clientId ?? 'unassigned'}>
                  <button
                    type="button"
                    disabled={!row.clientId}
                    onClick={() => row.clientId && navigate(`/clients/${encodeURIComponent(row.clientId)}`)}
                    className="w-full text-left disabled:cursor-default"
                  >
                    <span className="flex items-baseline justify-between gap-3">
                      <span className="flex items-baseline gap-2 min-w-0">
                        <span
                          className="pro-numeral text-[11px] font-bold shrink-0"
                          style={{ color: index === 0 ? 'var(--pro-primary)' : 'var(--pro-text-grey-light)' }}
                        >
                          {index + 1}
                        </span>
                        <span
                          className="text-[14px] font-medium truncate"
                          style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
                        >
                          {clientLabel(row, clientNames, t)}
                        </span>
                      </span>
                      <span className="pro-numeral text-[14px] font-semibold shrink-0" style={{ color: 'var(--pro-text-dark)' }}>
                        {formatTND(row.revenueTtc)}
                      </span>
                    </span>
                    <span className="mt-2 flex items-center gap-3">
                      <span className="h-[4px] rounded-full flex-1 overflow-hidden" style={{ background: 'var(--pro-grey-subtle)' }}>
                        <span
                          className="block h-full rounded-full"
                          style={{
                            width: `${Math.min(100, Math.max(2, row.revenueShare))}%`,
                            background: index === 0 ? 'var(--pro-primary)' : 'var(--pro-primary-light)',
                          }}
                        />
                      </span>
                      <span className="pro-numeral text-[12px] font-semibold shrink-0" style={{ color: 'var(--pro-primary)' }}>
                        {formatTND(row.commission)}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* ---------- Dernières commandes ---------- */}
      <section className="pro-card p-6 pro-rise-in" style={{ ['--pro-stagger-i' as string]: 5 }}>
        <div className="flex items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <IconBubble icon={ShoppingBag} />
            <h2
              className="text-[15px] font-semibold"
              style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree', letterSpacing: '-0.01em' }}
            >
              {t('pro.statistique.recent_orders', 'Commandes récentes')}
            </h2>
          </div>
          <Quiet label={t('pro.statistique.view_all', 'Voir tout')} onClick={() => navigate('/ordres')} />
        </div>
        {recentOrders.length === 0 ? (
          <Empty label={t('pro.dashboard.no_orders', 'Aucune commande pour le moment')} />
        ) : (
          <ul className="space-y-4">
            {recentOrders.map((order) => (
              <li key={order.code}>
                <button
                  type="button"
                  onClick={() => navigate(`/ordres/${encodeURIComponent(order.code)}`)}
                  className="w-full text-left flex items-center gap-4"
                >
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2.5 flex-wrap">
                      <span className="pro-numeral text-[13px] font-semibold" style={{ color: 'var(--pro-text-dark)' }}>
                        {order.code}
                      </span>
                      <CommandeStatusBadge status={order.status} />
                    </span>
                    <span className="block text-[12px] mt-1 truncate" style={{ color: 'var(--pro-text-light)', fontFamily: 'Figtree' }}>
                      {formatDate(order.date)} ·{' '}
                      {order.proClientId
                        ? clientNames.get(order.proClientId) ?? t('pro.statistique.unknown_client', 'Client supprimé')
                        : t('pro.statistique.no_client', 'Sans client')}
                    </span>
                  </span>
                  <span className="text-right shrink-0">
                    <span className="pro-numeral block text-[14px] font-semibold" style={{ color: 'var(--pro-text-dark)' }}>
                      {formatTND(order.pttc)}
                    </span>
                    <span className="pro-numeral block text-[12px] font-semibold" style={{ color: 'var(--pro-primary)' }}>
                      +{formatTND(order.totalCommission)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ============================================================
// Présentation — des icônes et des couleurs, mais zéro bordure
// ============================================================

/** Pastille d'icône : un aplat doux, pas un contour. */
function IconBubble({ icon: Icon, accent }: { icon: typeof Wallet; accent?: boolean }) {
  return (
    <span
      className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
      style={{
        background: accent ? 'var(--pro-primary)' : 'var(--pro-primary-subtle)',
        color: accent ? '#fff' : 'var(--pro-primary)',
      }}
    >
      <Icon className="w-[18px] h-[18px]" strokeWidth={2.1} />
    </span>
  );
}

function WalletTile({
  icon,
  label,
  value,
  accent,
}: {
  icon: typeof Wallet;
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="pro-card p-5">
      <IconBubble icon={icon} accent={accent} />
      <div className="text-[12.5px] mt-3.5 truncate" style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}>
        {label}
      </div>
      <div
        className="pro-numeral text-[19px] font-bold mt-1"
        style={{ color: accent ? 'var(--pro-primary)' : 'var(--pro-text-dark)' }}
      >
        {value}
      </div>
    </div>
  );
}

function InlineFact({
  icon: Icon,
  label,
  value,
  danger,
}: {
  icon: typeof Wallet;
  label: string;
  value: string;
  danger?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-2.5">
      <Icon className="w-4 h-4 shrink-0" style={{ color: danger ? 'var(--pro-danger)' : 'var(--pro-primary)' }} />
      <span className="text-[12.5px]" style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}>
        {label}
      </span>
      <span
        className="pro-numeral text-[14px] font-semibold"
        style={{ color: danger ? 'var(--pro-danger)' : 'var(--pro-text-dark)' }}
      >
        {value}
      </span>
    </span>
  );
}

function Metric({
  icon,
  label,
  value,
  hint,
  trend,
  accent,
  small,
  loading,
}: {
  icon: typeof Wallet;
  label: string;
  value: string;
  hint?: string;
  /** `null` = pas de base de comparaison → rien plutôt qu'un faux 0 %. */
  trend?: number | null;
  accent?: boolean;
  small?: boolean;
  loading?: boolean;
}) {
  return (
    <div className="pro-card p-5">
      <div className="flex items-start justify-between gap-2">
        <IconBubble icon={icon} accent={accent} />
        {trend != null && !loading && <Delta value={trend} />}
      </div>
      <div className="text-[12.5px] mt-3.5 truncate" style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}>
        {label}
      </div>
      <div
        className={`pro-numeral ${small ? 'text-[20px]' : 'text-[24px]'} font-bold leading-tight mt-1`}
        style={{ color: accent ? 'var(--pro-primary)' : 'var(--pro-text-dark)' }}
      >
        {loading ? '—' : value}
      </div>
      {hint && (
        <div className="text-[11.5px] mt-1.5 truncate" style={{ color: 'var(--pro-text-light)', fontFamily: 'Figtree' }}>
          {hint}
        </div>
      )}
    </div>
  );
}

/** Variation : texte coloré et flèche. Pas de pastille, pas de fond. */
function Delta({ value }: { value: number }) {
  const positive = value >= 0;
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[12px] font-semibold"
      style={{ color: positive ? '#14804A' : '#C2183C', fontFamily: 'Figtree' }}
    >
      <Icon className="w-3.5 h-3.5" strokeWidth={2.4} />
      {Math.abs(value).toFixed(1).replace('.', ',')} %
    </span>
  );
}

function Quiet({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group inline-flex items-center gap-1 text-[12.5px] font-semibold shrink-0"
      style={{ color: 'var(--pro-primary)', fontFamily: 'Figtree' }}
    >
      {label}
      <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}

function Empty({ label }: { label: string }) {
  return (
    <p className="py-10 text-center text-[13px]" style={{ color: 'var(--pro-text-light)', fontFamily: 'Figtree' }}>
      {label}
    </p>
  );
}
